import React, { useState, useEffect, useMemo, useRef } from "react";

const Hero = () => {
  const fullHistory = useMemo(
    () => [
      { role: "user", content: "What can AI do for my supply chain?" },
      {
        role: "assistant",
        content:
          "AI can help you reduce overstock, forecast demand, and automate reorder points across your warehouse network.",
      },
    ],
    []
  );

  const [userInput, setUserInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");

  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (messageIndex < fullHistory.length) {
      const currentMsg = fullHistory[messageIndex];
      let timeoutId;

      if (charIndex < currentMsg.content.length) {
        timeoutId = setTimeout(() => {
          setCurrentText((prev) => prev + currentMsg.content[charIndex]);
          setCharIndex((prev) => prev + 1);
        }, 30);
      } else {
        setChatHistory((prev) => [
          ...prev,
          { ...currentMsg, content: currentText },
        ]);
        setCurrentText("");
        setCharIndex(0);
        setMessageIndex((prev) => prev + 1);
      }

      return () => clearTimeout(timeoutId);
    }
  }, [charIndex, messageIndex, currentText, fullHistory]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  const displayMessages = [...chatHistory];
  if (messageIndex < fullHistory.length && currentText) {
    displayMessages.push({
      ...fullHistory[messageIndex],
      content: currentText,
    });
  }

  const sendMessage = async () => {
    if (!userInput.trim()) return;

    const newHistory = [...chatHistory, { role: "user", content: userInput }];
    setChatHistory(newHistory);
    setLoading(true);
    setUserInput("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/submitChatMessage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: newHistory,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.reply) {
        setChatHistory([
          ...newHistory,
          { role: "assistant", content: data.reply },
        ]);
      } else {
        setChatHistory([
          ...newHistory,
          {
            role: "assistant",
            content: "⚠️ No response generated from the AI.",
          },
        ]);
      }
    } catch (err) {
      console.error("Error sending message:", err);
      setChatHistory([
        ...newHistory,
        {
          role: "assistant",
          content: `⚠️ Something went wrong: ${err.message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="hero-pattern-dots relative min-h-screen px-4 sm:px-6 pt-24 sm:pt-32 pb-24 flex flex-col lg:flex-row items-center justify-center gap-12 overflow-hidden bg-primary dark:bg-darkBg transition-colors duration-300">
      {/* Decorative blobs */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-24 h-24 rounded-full blur-2xl bg-blue-300/20 dark:bg-blue-900/20 animate-drift" />
        <div className="absolute bottom-28 right-16 w-32 h-32 rounded-full blur-2xl bg-purple-400/30 dark:bg-purple-900/20 animate-rotate-glow" />
        <div className="absolute top-[50%] left-[60%] -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-gradient-to-br from-blue-100 via-transparent to-purple-100 dark:from-slate-700 dark:to-purple-900 blur-3xl opacity-30 animate-color-pulse" />
      </div>
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-gradient-to-tr from-blue-300/20 dark:from-blue-900/20 to-transparent blur-[100px] opacity-40 animate-[pulse_6s_ease-in-out_infinite]" />
      </div>

      {/* Hero Text */}
      <div className="flex-1 max-w-xl text-center lg:text-left z-10">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-[DM Sans] font-extrabold leading-tight mb-4 animate-fade-in-up text-textDark dark:text-textLight">
          See What AI Can Do <br className="hidden md:block" />
          For Your Business Today
        </h1>
        <p className="font-[Inter] text-base sm:text-lg mb-8 text-gray-700 dark:text-gray-300 animate-fade-in-up">
          Interact with Blackbear's smart assistant powered by OpenAI GPT-4.
          Discover insights, automate decisions, and simplify operations — all
          in real-time.
        </p>

        <div className="flex flex-wrap justify-center sm:justify-start gap-2 animate-fade-in-up">
          {[
            "How can AI help my operations?",
            "Show me a supply chain use case",
            "Can I automate purchase orders?",
            "Explain predictive inventory",
          ].map((item, idx) => (
            <button
              key={idx}
              onClick={() => setUserInput(item)}
              className="w-full sm:w-auto px-4 py-2 bg-blue-700 dark:bg-blue-500 text-white text-sm rounded-full font-medium transition hover:bg-black dark:hover:bg-slate-800 hover:shadow-sm hover:scale-[1.02]"
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Widget */}
      <div className="flex-1 w-full max-w-xl bg-white dark:bg-darkSecondary rounded-xl shadow-2xl p-4 sm:p-6 z-10 animate-fade-in-up border border-blue-100/40 dark:border-slate-700 backdrop-blur-md transition-colors">
        <div className="font-semibold text-left mb-2 text-gray-600 dark:text-gray-200">
          Blackbear AI Assistant
        </div>
        <div
          ref={chatContainerRef}
          className="border border-slate-300 dark:border-slate-800 rounded-lg h-60 sm:h-56 p-3 sm:p-4 text-sm text-gray-600 dark:text-gray-200 overflow-y-auto bg-gray-50 dark:bg-slate-900/70 space-y-3 shadow-inner transition-colors"
        >
          {displayMessages.map(
            (msg, idx) =>
              msg?.role &&
              msg?.content && (
                <p key={idx} className="whitespace-pre-line">
                  <span className="font-medium text-black dark:text-white">
                    {msg.role === "user" ? "You" : "Blackbear AI"}:
                  </span>{" "}
                  {msg.content}
                </p>
              )
          )}
          {loading && (
            <p className="italic text-gray-400 dark:text-gray-500 shining-text">
              Blackbear AI is thinking...
            </p>
          )}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            placeholder="Type your question..."
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            className="flex-1 border border-slate-300 dark:border-slate-700 px-4 py-2 rounded-md text-sm focus:outline-none bg-white dark:bg-slate-900 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
          />
          <button
            onClick={sendMessage}
            className="bg-blue-700 dark:bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-[#2e2e2e] dark:hover:bg-slate-800 text-sm w-full sm:w-auto transition-colors"
          >
            Ask
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
